import { isBreakpointEqualOrSmaller } from '../../utils';
import { DcUiWebStage } from './dc-ui-web-stage';

const SLIDE_TRANSITION_TIME = 300;

const debugLog = (stage: DcUiWebStage, message: string) => {
  if (stage.debug) {
    console.log(`[DcUiWebStage Utils] ${message}`);
  }
};

/**
 * Returns the active slide element and its index.
 *
 * @param {DcUiWebStage} stage
 * @returns {element: HTMLElement, index: number}
 */
const getActiveSlide = (stage: DcUiWebStage) => {
  const activeSlide = stage.el.querySelector('dc-ui-web-stage-item.active');
  const index = Array.from(stage.el.querySelectorAll('dc-ui-web-stage-item')).indexOf(stage.el.querySelector('dc-ui-web-stage-item.active'));

  return {
    element: activeSlide as HTMLElement,
    index,
  };
};

/**
 * Initializes the slides.
 *
 * @param {DcUiWebStage} stage
 */
export const initSlides = (stage: DcUiWebStage) => {
  stage.stageWidth = stage.stageWrapper.offsetWidth;
  stage.slidesWrapper.style.width = `${stage.stageWidth * stage.numSlides}px`;

  stage.slides.forEach((slide: HTMLElement, index) => {
    slide.style.width = `${stage.stageWidth}px`;

    slide.setAttribute('data-slide-index', `${index}`);

    if (!stage.hasRendered && stage.navigationDotList) {
      const dot = createNavigationDotElement(stage, index);
      stage.navigationDotList.appendChild(dot);
      stage.navigationDotList.children[0].classList.add('active');
    }
  });

  (stage.slides[0] as HTMLElement).classList.add('active', 'visited');
};

/**
 * Creates a navigation dot element (for the navigation dot list) from a given slide index.
 *
 * @param {DcUiWebStage} stage - The stage object.
 * @param {number} index - The slide index.
 */
const createNavigationDotElement = (stage: DcUiWebStage, index: number) => {
  const button = document.createElement('button');
  button.classList.add('dot');
  button.setAttribute('data-slide-index', `${index}`);
  button.setAttribute('aria-label', `Go to slide ${index + 1}`);

  button.addEventListener('click', () => {
    goToSlide(stage, index);
  });

  const listItem = document.createElement('li');
  listItem.appendChild(button);

  return listItem;
};

/**
 * Initializes touch events.
 *
 * This function adds touchstart, touchmove, and touchend event listeners to the stage's slides wrapper.
 * It uses these events to detect swipes to the left or right. If a swipe is detected, it triggers the
 * appropriate action to move to the next or previous slide.
 *
 * @param {DcUiWebStage} stage - The stage object.
 *
 * @returns {void} - This function does not return anything; it directly adds event listeners
 * to the stage's slides wrapper.
 */
export const setupEvents = (stage: DcUiWebStage) => {
  const minSwipeDistance = 50;

  stage.slidesWrapper.addEventListener(
    'touchstart',
    (e: TouchEvent) => {
      stage.swipeStartX = e.touches[0].screenX;
    },
    { passive: true },
  );

  stage.slidesWrapper.addEventListener(
    'touchmove',
    (e: TouchEvent) => {
      stage.swipeEndX = e.touches[0].screenX;
    },
    { passive: true },
  );

  stage.slidesWrapper.addEventListener(
    'touchend',
    () => {
      if (!stage.swipeStartX || !stage.swipeEndX) {
        return;
      }
      if (stage.swipeStartX - stage.swipeEndX > minSwipeDistance) {
        goToNextSlide(stage);
      } else if (stage.swipeEndX - stage.swipeStartX > minSwipeDistance) {
        goToPrevSlide(stage);
      }
      stage.swipeStartX = undefined;
      stage.swipeEndX = undefined;
    },
    { passive: true },
  );

  setupAutoSlide(stage);
};

/**
 * Adjusts the widths of the stage and its slides.
 *
 * This function calculates the width of the stage and sets the width of each slide to match it.
 * It also sets the width of the slides wrapper to be the product of the stage width and the number of slides.
 * If there is an active slide, it adjusts the transform of the slides wrapper to ensure the active slide is in view.
 * It temporarily removes the transition from the slides wrapper to prevent any animation during this adjustment.
 *
 * @param {DcUiWebStage} stage - The stage object.
 *
 * @returns {void} - This function does not return anything; it directly manipulates the style
 * of the stage and its slides.
 */
export const adjustContainerAndSlideWidths = (stage: DcUiWebStage, animate = false) => {
  if (!animate) {
    stage.slidesWrapper.classList.add('no-transition');
    setTimeout(() => {
      stage.slidesWrapper.classList.remove('no-transition');
    }, 0);
  }

  stage.stageWidth = stage.stageWrapper.offsetWidth;

  stage.slides.forEach((slide: HTMLElement) => {
    slide.style.width = `${stage.stageWidth}px`;
  });

  const { element: activeSlideElement, index: activeSlideIndex } = getActiveSlide(stage);

  stage.slidesWrapper.style.width = `${stage.stageWidth * stage.numSlides}px`;
  stage.stageWrapper.style.height = `${activeSlideElement.scrollHeight}px`;

  stage.slidesWrapper.style.transform = `translateX(-${stage.stageWidth * activeSlideIndex}px)`;
};

/**
 * Navigates to a specific slide.
 *
 * This function finds the index of the slide with the given ID,
 * calculates the transform needed to bring that slide into view, and applies that transform to the slides wrapper.
 * It also updates the active class on the slides to reflect the new active slide.
 *
 * @param {DcUiWebStage} stage - The stage object.
 * @param {string} slideIndex - The ID of the slide to navigate to.
 *
 * @returns {void} - This function does not return anything; it directly manipulates the style
 * of the stage and its slides.
 */
export const goToSlide = (stage: DcUiWebStage, slideIndex: number) => {
  debugLog(stage, `Going to slide ${slideIndex}`);
  adjustContainerAndSlideWidths(stage, true);

  const slides = stage.el.querySelectorAll('dc-ui-web-stage-item');

  slideIndex = Math.max(Math.min(stage.numSlides - 1, slideIndex), 0);

  stage.slidesWrapper.classList.add('sliding');
  stage.slidesWrapper.style.transform = `translateX(-${stage.stageWidth * slideIndex}px)`;

  slides.forEach((slide: HTMLElement) => {
    slide.tabIndex = -1;
    slide.classList.remove('active');
  });
  slides[slideIndex].classList.add('active', 'visited');

  stage.navigationDotList.querySelectorAll('li').forEach(dot => {
    dot.classList.remove('active');
  });
  stage.navigationDotList.children[slideIndex].classList.add('active');

  stage.activeSlideIndex = slideIndex;

  adjustContainerAndSlideWidths(stage, true);

  setTimeout(() => {
    slides.forEach((slide: HTMLElement) => {
      slide.tabIndex = 0;
    });
    stage.slidesWrapper.classList.remove('sliding');
  }, SLIDE_TRANSITION_TIME);
};

/**
 * Moves to the next slide.
 *
 * @param {DcUiWebStage} stage - The stage object.
 *
 * @returns {void} - This function does not return anything; it directly manipulates the style
 * of the stage's slides wrapper.
 */
export const goToNextSlide = (stage: DcUiWebStage) => {
  const nextIndex = (stage.activeSlideIndex + 1) % stage.numSlides;
  goToSlide(stage, nextIndex);
};

/**
 * Moves to the previous slide.
 *
 * @param {DcUiWebStage} stage - The stage object.
 *
 * @returns {void} - This function does not return anything; it directly manipulates the style
 * of the stage's slides wrapper.
 */
export const goToPrevSlide = (stage: DcUiWebStage) => {
  const prevIndex = (stage.numSlides + stage.activeSlideIndex - 1) % stage.numSlides;
  goToSlide(stage, prevIndex);
};

export const setupAutoSlide = (stage: DcUiWebStage) => {
  const isMobile = () => isBreakpointEqualOrSmaller('md');

  let animationFrameId: number | null = null;
  let lastSlideTime = 0;

  const autoSlide = (currentTime: number) => {
    if (stage.autoSlide && !isMobile()) {
      if (currentTime - lastSlideTime >= stage.autoSlideInterval) {
        goToNextSlide(stage);
        lastSlideTime = currentTime;
      }
      animationFrameId = requestAnimationFrame(autoSlide);
    }
  };

  const startAutoSlide = () => {
    if (stage.autoSlide && !isMobile() && !animationFrameId) {
      lastSlideTime = performance.now();
      animationFrameId = requestAnimationFrame(autoSlide);
    }
  };

  const stopAutoSlide = () => {
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
  };

  const handleResize = () => {
    stopAutoSlide();
    startAutoSlide();
  };

  const pauseAutoSlide = () => {
    debugLog(stage, 'Hovering over stage, pausing auto slide');
    stopAutoSlide();
  };

  const resumeAutoSlide = () => {
    debugLog(stage, 'Leaving stage, resuming auto slide');
    startAutoSlide();
  };

  if (stage.autoSlide) {
    startAutoSlide();

    if (stage.pauseOnHover) {
      stage.el.addEventListener('mouseenter', pauseAutoSlide);
      stage.el.addEventListener('mouseleave', resumeAutoSlide);
    }

    stage.el.addEventListener('focusin', pauseAutoSlide);
    stage.el.addEventListener('focusout', resumeAutoSlide);

    window.addEventListener('resize', handleResize);
  }
};
